import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon, Who } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Drawer, Summary, Tabs, EmptyState } from './Overlay';
import { STATUS_META } from './BookingList';
import { text } from './peopleCopy';

/*
 * Providers — arketipe B paket client (`providers` di screens.js).
 *
 * Kolom, filter, aksi baris dan drawer tiga tab mengikuti prototipe. Dua hal
 * yang tidak bisa sama karena datanya tidak ada:
 *
 *  - Aksi "Review documents" prototipe membuka antrean persetujuan per berkas.
 *    UserDocument tidak punya kolom status, jadi yang bisa dilakukan sekarang
 *    adalah menyetujui atau menangguhkan providernya — bukan menilai berkas
 *    satu per satu.
 *  - Formulir Edit prototipe ikut mengubah layanan dan area. Keduanya diisi
 *    provider sendiri lewat aplikasi (UserCategory dan ServiceArea), jadi di
 *    sini yang bisa diubah nama, telepon dan email.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String,
                  $duty: String, $verified: String, $categoryId: Int) {
  getProviders(currentPage: $currentPage, limit: $limit, searchList: $searchList,
               duty: $duty, verified: $verified, categoryId: $categoryId) {
    count total onDuty status errorMessage
    results {
      id name email phone duty verified banned services primaryCategoryId area primaryArea areaFromServiceArea
      docCount jobsToday jobsTotal earned rating joined
    }
  }
}`;

const Q_CATS = `{ getActiveCategories { results { id name } } }`;
const Q_AREAS = `{ getAdminServiceAreas(currentPage: 1, limit: 1000, state: "active") {
  status results { id name }
} }`;

const Q_JOBS = `query ($partnerId: String) {
  getAllBookings(currentPage: 1, limit: 25, partnerId: $partnerId) {
    status
    results { id status totalFare createdAt userDetails { firstName lastName } }
  }
}`;

const M_STATUS = `mutation ($userId: String!, $userStatus: String!, $reason: String) {
  setProviderStatus(userId: $userId, userStatus: $userStatus, reason: $reason) { status errorMessage }
}`;

const M_INVITE = `mutation ($firstName: String!, $lastName: String,
                   $phoneDialCode: String!, $phoneNumber: String!,
                   $primaryCategoryId: Int, $area: String) {
  inviteProvider(firstName: $firstName, lastName: $lastName,
                 phoneDialCode: $phoneDialCode, phoneNumber: $phoneNumber,
                 primaryCategoryId: $primaryCategoryId, area: $area) { status errorMessage }
}`;

const M_EDIT = `mutation ($userId: String!, $firstName: String!, $lastName: String,
                 $phoneDialCode: String!, $phoneNumber: String!,
                 $primaryCategoryId: Int!, $area: String!) {
  updateAdminProvider(userId: $userId, firstName: $firstName, lastName: $lastName,
    phoneDialCode: $phoneDialCode, phoneNumber: $phoneNumber,
    primaryCategoryId: $primaryCategoryId, area: $area) { status errorMessage }
}`;

const M_CALL = `mutation ($userId: String!) {
  logProviderCall(userId: $userId) { status errorMessage }
}`;

const SUSPEND_REASONS = ['Documents expired', 'Documents rejected', 'Customer complaints',
  'Unsafe conduct', 'Fraud suspected', 'Requested by provider', 'Other'];

const fmt = n => Number(n || 0).toLocaleString('en-US');
const money = n => `AED ${fmt(Math.round(Number(n) || 0))}`;
const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? '—' : d.toISOString().slice(0, 10);
};

const DUTY_META = { job: ['On job', 'amber'], idle: ['Idle', 'ok'], offline: ['Offline', ''] };
const DOC_META = {
  yes: ['Verified', 'ok'], pending: ['Pending', 'warn'],
  rejected: ['Rejected', 'danger'], suspended: ['Suspended', 'danger']
};

const Tag = ({ meta, value, locale }) => {
  const [label, kind] = meta[value] || [value, ''];
  const ar = { 'On job': 'في مهمة', Idle: 'متاح', Offline: 'غير متصل', Verified: 'موثّق', Pending: 'قيد الانتظار', Rejected: 'مرفوض', Suspended: 'موقوف' };
  return <span className={`tag ${kind}`}><i />{locale === 'ar' ? (ar[label] || label) : label}</span>;
};

class Providers extends React.Component {
  state = {
    rows: [], count: 0, total: 0, onDuty: 0, page: 1, perPage: 10, search: '',
    duty: '', verified: '', categoryId: '', cats: [], areas: [],
    loading: true, error: null,
    viewing: null, tab: 0, jobs: null,
    approving: null, suspending: null, reason: '', saving: false, notice: null,
    // Satu formulir dipakai untuk undang dan sunting; `form.id` yang membedakan.
    form: null, formError: null, calling: null
  };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  componentDidMount() { this.load(); this.loadCats(); this.loadAreas(); }

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    duty: this.state.duty || undefined,
    verified: this.state.verified || undefined,
    categoryId: this.state.categoryId ? Number(this.state.categoryId) : undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getProviders;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || 'Could not load providers.' });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0,
      total: res.total || 0, onDuty: res.onDuty || 0, loading: false
    });
  };

  loadCats = async () => {
    const json = await gql(Q_CATS).catch(() => null);
    const cats = json && json.data && json.data.getActiveCategories
      && json.data.getActiveCategories.results;
    if (cats && cats.length) this.setState({ cats });
  };

  loadAreas = async () => {
    const json = await gql(Q_AREAS).catch(() => null);
    const res = json && json.data && json.data.getAdminServiceAreas;
    if (res && res.status === 200) this.setState({ areas: res.results || [] });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  view = async p => {
    this.setState({ viewing: p, tab: 0, jobs: null });
    const json = await gql(Q_JOBS, { partnerId: p.id }).catch(() => null);
    const res = json && json.data && json.data.getAllBookings;
    this.setState({ jobs: (res && res.results) || [] });
  };

  /*
   * Satu formulir untuk undang dan sunting. Bedanya cuma `id`: ada berarti
   * menyunting, kosong berarti membuat.
   */
  openInvite = () => this.setState({
    form: {
      id: null, fullName: '', phone: '',
      primaryCategoryId: (this.state.cats[0] || {}).id || '',
      area: (this.state.areas[0] || {}).name || ''
    },
    formError: null, notice: null
  });

  openEdit = r => {
    this.setState({
      form: {
        id: r.id, fullName: r.name || '', phone: r.phone || '',
        primaryCategoryId: r.primaryCategoryId || '',
        area: r.primaryArea || ''
      },
      formError: null, notice: null
    });
  };

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submitForm = async () => {
    const f = this.state.form;
    if (!f.fullName.trim() || !f.phone.trim() || !f.primaryCategoryId || !f.area) {
        this.setState({ formError: this.tx('Full name, phone, primary service and base area are required.', 'الاسم الكامل والهاتف والخدمة الأساسية والمنطقة الأساسية مطلوبة.') });
      return;
    }
    const [firstName, ...last] = f.fullName.trim().split(/\s+/);
    const match = /^(\+\d{1,4})\s*(.*)$/.exec(f.phone.trim());
    const phoneDialCode = match ? match[1] : '+971';
    const phoneNumber = String(match ? match[2] : f.phone).replace(/\D/g, '');
    if (!phoneNumber) {
      this.setState({ formError: this.tx('Enter a valid phone number.', 'أدخل رقم هاتف صحيحاً.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const vars = {
      ...(f.id ? { userId: f.id } : {}),
      firstName, lastName: last.join(' '), phoneDialCode, phoneNumber,
      primaryCategoryId: Number(f.primaryCategoryId), area: f.area
    };

    const json = await gql(f.id ? M_EDIT : M_INVITE, vars).catch(() => null);
    const res = json && json.data && (f.id ? json.data.updateAdminProvider : json.data.inviteProvider);
    this.setState({ saving: false });

    if (!res || res.status !== 200) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save.', 'تعذر الحفظ.') });
      return;
    }
    const who = f.fullName.trim();
    this.setState({
      form: null,
      notice: f.id
      ? { tone: 'amber', text: this.tx(`${who} updated.`, `تم تحديث ${who}.`) }
        : { tone: 'amber', text: this.tx(`${who} added. They can sign in to the provider app with ${phoneDialCode} ${phoneNumber}, and stay pending until approved.`, `تمت إضافة ${who}. يمكنه تسجيل الدخول إلى تطبيق مقدم الخدمة بالرقم ${phoneDialCode} ${phoneNumber} وسيبقى قيد الانتظار حتى الموافقة.`) }
    });
    this.load(true);
  };

  openCall = async provider => {
    this.setState({ calling: provider });
    const json = await gql(M_CALL, { userId: provider.id }).catch(() => null);
    const res = json && json.data && json.data.logProviderCall;
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: this.tx('Could not log this call.', 'تعذر تسجيل الاتصال.') } });
    }
  };

  copyNumber = async () => {
    const provider = this.state.calling;
    if (!provider) return;
    try {
      await navigator.clipboard.writeText(provider.phone.replace(/\s/g, ''));
      this.setState({
        calling: null,
        notice: { tone: 'amber', text: this.tx(`Copied ${provider.phone}.`, `تم نسخ ${provider.phone}.`) }
      });
    } catch (error) {
      this.setState({ notice: { tone: 'danger', text: this.tx('Could not copy the phone number.', 'تعذر نسخ رقم الهاتف.') } });
    }
  };

  save = async (target, userStatus, reason) => {
    this.setState({ saving: true });
    const json = await gql(M_STATUS, { userId: target.id, userStatus, reason }).catch(() => null);
    const res = json && json.data && json.data.setProviderStatus;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not save.', 'تعذر الحفظ.') } });
      return;
    }
    const who = target.name || target.email;
    this.setState({
      approving: null, suspending: null, reason: '',
      notice: userStatus === 'active'
        ? { tone: 'amber', text: `${who} approved — they now appear in provider search.` }
        : { tone: 'danger', text: `${who} suspended — ${reason}. They are offline and will receive no jobs.` }
    });
    this.load(true);
  };

  columns() {
    return [
      {
        key: 'name', label: this.tx('Provider', 'مقدم الخدمة'), sortable: true,
        render: r => <Who name={r.name || this.tx('Unnamed', 'بدون اسم')} sub={r.id.slice(0, 8)} />
      },
      { key: 'duty', label: this.tx('Duty', 'المهمة'), render: r => <Tag meta={DUTY_META} value={r.duty} locale={this.locale()} /> },
      {
        key: 'services', label: this.tx('Service', 'الخدمة'), sortable: true,
        render: r => r.services
          ? <span title={r.services} style={{
              display: 'inline-block', maxWidth: 160, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'
            }}>{r.services}</span>
          : <span className="muted">{this.tx('None set', 'غير محدد')}</span>
      },
      {
        key: 'area', label: this.tx('Area', 'المنطقة'), sortable: true,
        render: r => r.area
          ? (
            <span title={r.areaFromServiceArea ? r.area : `${r.area} (from profile, no service area set)`}
              style={{
                display: 'inline-block', maxWidth: 160, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'
              }}>
              {r.area}{!r.areaFromServiceArea && <em style={{ color: 'var(--tx-3)' }}> *</em>}
            </span>
          )
          : <span className="muted">—</span>
      },
      { key: 'jobsToday', label: this.tx('Jobs today', 'الطلبات اليوم'), align: 'end', sortable: true },
      {
        key: 'rating', label: this.tx('Rating', 'التقييم'), align: 'end', sortable: true,
        render: r => r.rating
          ? <span className="num">{r.rating.toFixed(1)}</span>
          : <span className="muted">—</span>
      },
      { key: 'verified', label: this.tx('Docs', 'المستندات'), render: r => <Tag meta={DOC_META} value={r.verified} locale={this.locale()} /> },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title={this.tx('View profile', 'عرض الملف')}
              aria-label={`View ${r.name || r.email}`}
              onClick={() => this.view(r)}><Icon name="eye" /></button>
            {r.phone && (
              <button className="btn icon" type="button" title={`${this.tx('Call', 'اتصال')} ${r.phone}`}
                aria-label={`Call ${r.name || r.email}`}
                onClick={() => this.openCall(r)}><Icon name="phone" /></button>
            )}
            {/* Prototipe: perisai kalau dokumennya menunggu, pensil kalau tidak.
                Menangguhkan pindah ke dalam drawer — memutus penghasilan orang
                tidak pantas jadi ikon ketiga di baris yang bisa tersenggol. */}
            {r.verified === 'pending' || r.verified === 'rejected'
              ? (
                <a className="btn icon" title={this.tx('Review documents', 'مراجعة المستندات')}
                  aria-label={`Review documents for ${r.name || r.email}`}
                  href="/admin/provider-approvals">
                  <Icon name="shield" />
                </a>
              )
              : (
                <button className="btn icon" type="button" title={this.tx('Edit provider', 'تعديل مقدم الخدمة')}
                  aria-label={`Edit ${r.name || r.email}`}
                  onClick={() => this.openEdit(r)}>
                  <Icon name="edit" />
                </button>
              )}
          </span>
        )
      }
    ];
  }

  renderDrawer() {
    const { viewing: p, tab, jobs } = this.state;

    return (
      <Drawer
        title={p.name || this.tx('Unnamed provider', 'مقدم خدمة بدون اسم')}
        sub={[p.id.slice(0, 8), p.services, p.area].filter(Boolean).join(' · ')}
        headExtra={<Tag meta={DUTY_META} value={p.duty} locale={this.locale()} />}
        tabs={<Tabs labels={[this.tx('Overview', 'نظرة عامة'), this.tx('Jobs', 'الطلبات'), this.tx('Earnings', 'الأرباح')]} active={tab}
          onChange={i => this.setState({ tab: i })} />}
        onClose={() => this.setState({ viewing: null, jobs: null })}
        actions={
          <div className="sp">
            <button className="btn ghost" type="button"
              onClick={() => this.setState({ viewing: null, jobs: null })}>{this.tx('Close', 'إغلاق')}</button>
            {p.phone && (
              <button className="btn ghost" type="button"
                onClick={() => this.setState({ viewing: null, jobs: null }, () => this.openCall(p))}>
                <Icon name="phone" />{this.tx('Call', 'اتصال')}
              </button>
            )}
            {p.verified === 'yes'
              ? <button className="btn ghost" type="button"
                  onClick={() => this.setState({ viewing: null, suspending: p, reason: '' })}>
                  {this.tx('Suspend', 'إيقاف')}
                </button>
              : <button className="btn" type="button"
                  onClick={() => this.setState({ viewing: null, approving: p })}>
                  {this.tx('Approve provider', 'الموافقة على مقدم الخدمة')}
                </button>}
          </div>
        }
      >
        {tab === 0 && (
          <div>
            <Summary pairs={[
              [this.tx('Provider ID', 'معرّف مقدم الخدمة'), <span className="mono">{p.id}</span>],
              [this.tx('Phone', 'الهاتف'), <span className="mono">{p.phone || '—'}</span>],
              [this.tx('Email', 'البريد الإلكتروني'), p.email || '—'],
              [this.tx('Duty', 'المهمة'), <Tag meta={DUTY_META} value={p.duty} locale={this.locale()} />],
              [this.tx('Account', 'الحساب'), <Tag meta={DOC_META} value={p.verified} locale={this.locale()} />],
              [this.tx('Documents uploaded', 'المستندات المرفوعة'), p.docCount
                ? `${p.docCount} ${this.tx('file', 'ملف')}${p.docCount === 1 ? '' : this.tx('s', 'ات')}`
                : this.tx('None', 'لا يوجد')],
              [this.tx('Services', 'الخدمات'), p.services || this.tx('None set', 'غير محدد')],
              [this.tx('Area', 'المنطقة'), p.area
                ? `${p.area}${p.areaFromServiceArea ? '' : ' (from profile)'}`
                : '—'],
              [this.tx('Rating', 'التقييم'), p.rating ? p.rating.toFixed(1) : this.tx('Not rated yet', 'لم يُقيّم بعد')],
              [this.tx('Jobs today', 'الطلبات اليوم'), String(p.jobsToday)],
              [this.tx('Jobs completed', 'الطلبات المكتملة'), String(p.jobsTotal)],
              [this.tx('Joined', 'تاريخ الانضمام'), day(p.joined)],
              ...(p.banned ? [[this.tx('Banned', 'محظور'), <span className="tag danger"><i />{this.tx('Banned', 'محظور')}</span>]] : [])
            ]} />
            {p.verified === 'pending' && (
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)' }}>
                {this.tx('Pending providers never appear in provider search, so they receive no jobs until approved.', 'لا يظهر مقدمو الخدمة قيد الانتظار في البحث ولا يتلقون طلبات حتى تتم الموافقة عليهم.')}
              </p>
            )}
          </div>
        )}

        {tab === 1 && (
          jobs === null
            ? <p style={{ color: 'var(--tx-3)' }}>{this.tx('Loading…', 'جارٍ التحميل…')}</p>
            : jobs.length === 0
              ? <EmptyState icon="inbox" title={this.tx('No jobs yet', 'لا توجد طلبات بعد')}
                  msg={this.tx('This provider has never been assigned a booking.', 'لم يتم إسناد أي حجز إلى مقدم الخدمة هذا.')} />
              : (
                <table className="tbl" style={{ minWidth: 0 }}>
                  <thead>
                    <tr><th>{this.tx('Ref', 'المرجع')}</th><th>{this.tx('Customer', 'العميل')}</th><th>{this.tx('Status', 'الحالة')}</th><th className="right">{this.tx('Fare', 'الأجرة')}</th></tr>
                  </thead>
                  <tbody>
                    {jobs.map(b => {
                      const [label, kind] = STATUS_META[b.status] || [b.status, ''];
                      const who = b.userDetails
                        ? `${b.userDetails.firstName || ''} ${b.userDetails.lastName || ''}`.trim()
                        : '';
                      return (
                        <tr key={b.id}>
                          <td><span className="mono">BK-{b.id}</span></td>
                          <td>{who || <span className="muted">{this.tx('Unknown', 'غير معروف')}</span>}</td>
                          <td><span className={`tag ${kind}`}><i />{label}</span></td>
                          <td className="right">
                            <span className="num">{b.totalFare ? money(b.totalFare) : '—'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
        )}

        {/* Prototipe menampilkan daftar payout di sini. Tabel Payout menyimpan
            METODE pembayaran, bukan transfer, jadi yang bisa ditampilkan adalah
            hasil yang sudah dikerjakan. Daftar transfer per provider menunggu
            layar Payouts. */}
        {tab === 2 && (
          <div>
            <Summary pairs={[
              [this.tx('Jobs completed', 'الطلبات المكتملة'), String(p.jobsTotal)],
              [this.tx('Earned after commission', 'الأرباح بعد العمولة'), money(p.earned)],
              [this.tx('Average per job', 'المتوسط لكل طلب'), p.jobsTotal ? money(p.earned / p.jobsTotal) : '—']
            ]} />
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)' }}>
              Earned is the fare of completed jobs less the platform commission.
              Individual transfers appear on the Payouts screen.
            </p>
          </div>
        )}
      </Drawer>
    );
  }

  render() {
    const {
      rows, count, total, onDuty, page, perPage, loading, error,
      duty, verified, categoryId, cats, areas,
      viewing, approving, suspending, reason, saving, notice, form, formError, calling
    } = this.state;

    return (
      <AdminShell
        current="providers"
        breadcrumb={[this.tx('People', 'الأشخاص'), this.tx('Providers', 'مقدمو الخدمة')]}
        title={this.tx('Providers', 'مقدمو الخدمة')}
        lead={this.tx(`${fmt(total)} registered, ${fmt(onDuty)} on duty now.`, `${fmt(total)} مسجلون، ${fmt(onDuty)} في مهمة الآن.`)}
        actions={[
          <a className="btn ghost" href="/siteadmin/partners" key="c">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="i" onClick={this.openInvite}>
            <Icon name="plus" />{this.tx('Add provider', 'إضافة مقدم خدمة')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {viewing && this.renderDrawer()}

        {calling && (
          <Modal
            icon="phone" narrow showClose
            title={`${this.tx('Call', 'اتصال')} ${calling.name || calling.email}`}
            sub={`${calling.id.slice(0, 8)}${calling.area ? ` · ${calling.area}` : ''}`}
            confirmLabel={this.tx('Copy number', 'نسخ الرقم')}
            cancelLabel={this.tx('Close', 'إغلاق')}
            onConfirm={this.copyNumber}
            onClose={() => this.setState({ calling: null })}
          >
            <div style={{ textAlign: 'center', padding: 'var(--sp-4) 0' }}>
              <strong className="mono" dir="ltr" style={{ fontSize: 'var(--fs-xl)' }}>
                {calling.phone}
              </strong>
              <p style={{ color: 'var(--tx-3)', marginTop: 'var(--sp-4)' }}>
                {this.tx('Calls placed from the panel are logged against this provider.', 'يتم تسجيل المكالمات التي تتم من لوحة التحكم على حساب مقدم الخدمة هذا.')}
              </p>
            </div>
          </Modal>
        )}

        {form && (
          <Modal
            icon="edit" wide showClose
            title={form.id ? `${this.tx('Edit', 'تعديل')} “${form.fullName}”` : this.tx('Add provider', 'إضافة مقدم خدمة')}
            confirmLabel={form.id ? this.tx('Save changes', 'حفظ التغييرات') : this.tx('Create', 'إنشاء')}
            busy={saving}
            disabled={!form.fullName.trim() || !form.phone.trim()
              || !form.primaryCategoryId || !form.area}
            onConfirm={this.submitForm}
            onClose={() => this.setState({ form: null, formError: null })}
          >
            {formError && (
              <div className="notice danger">
                <span className="ni">!</span><span className="nt">{formError}</span>
              </div>
            )}
            <div className="form-grid two">
              <div className="field">
                <label>{this.tx('Full name', 'الاسم الكامل')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input className="inp" value={form.fullName}
                  onChange={e => this.setForm('fullName', e.target.value)} />
              </div>
              <div className="field">
                <label>{this.tx('Phone', 'الهاتف')} <span style={{ color: 'var(--danger)' }}>*</span></label>
                <input className="inp" dir="ltr" value={form.phone}
                  placeholder="+971 5x xxx xxxx"
                  onChange={e => this.setForm('phone', e.target.value)} />
              </div>
              <div className="field">
                <label>{this.tx('Primary service', 'الخدمة الأساسية')}</label>
                <select className="inp" value={form.primaryCategoryId}
                  onChange={e => this.setForm('primaryCategoryId', e.target.value)}>
                  <option value="">{this.tx('Choose a service…', 'اختر خدمة…')}</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{this.tx('Base area', 'المنطقة الأساسية')}</label>
                <select className="inp" value={form.area}
                  onChange={e => this.setForm('area', e.target.value)}>
                  <option value="">{this.tx('Choose an area…', 'اختر منطقة…')}</option>
                  {form.area && !areas.some(a => a.name === form.area)
                    && <option value={form.area}>{form.area}</option>}
                  {areas.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>
            </div>
          </Modal>
        )}

        {approving && (
          <Modal
            icon="shield"
            title={`${this.tx('Approve', 'الموافقة على')} ${approving.name || approving.email}?`}
            sub={this.tx('They will start appearing in provider search and can receive jobs.', 'سيظهر في بحث مقدمي الخدمة ويمكنه استقبال الطلبات.')}
            confirmLabel={this.tx('Approve provider', 'الموافقة على مقدم الخدمة')}
            busy={saving}
            onConfirm={() => this.save(approving, 'active')}
            onClose={() => this.setState({ approving: null })}
          >
            <Summary pairs={[
              ['Documents uploaded', approving.docCount
                ? `${approving.docCount} file${approving.docCount === 1 ? '' : 's'}`
                : 'None'],
              ['Services', approving.services || 'None set'],
              ['Area', approving.area || '—']
            ]} />
            {!approving.docCount && (
              <span className="hint">
                This provider has uploaded no documents. Approving anyway lets them
                take jobs unverified.
              </span>
            )}
          </Modal>
        )}

        {suspending && (
          <Modal
            icon="x" tone="danger"
            title={`${this.tx('Suspend', 'إيقاف')} ${suspending.name || suspending.email}?`}
            sub={this.tx('They go offline immediately and receive no further jobs.', 'سيصبح غير متصل فوراً ولن يتلقى طلبات أخرى.')}
            confirmLabel={this.tx('Suspend provider', 'إيقاف مقدم الخدمة')}
            busy={saving}
            disabled={!reason}
            onConfirm={() => this.save(suspending, 'inactive', reason)}
            onClose={() => this.setState({ suspending: null, reason: '' })}
          >
            <div className="field">
              <label>{this.tx('Suspension reason', 'سبب الإيقاف')}</label>
              <select className="inp" value={reason}
                onChange={e => this.setState({ reason: e.target.value })}>
                <option value="">{this.tx('Choose a reason…', 'اختر سبباً…')}</option>
                {SUSPEND_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {!reason && <span className="hint">A reason is required. It is written to the audit log.</span>}
            </div>
            {suspending.duty === 'job' && (
              <div className="notice danger" style={{ marginBottom: 0 }}>
                <span className="ni">!</span>
                <span className="nt">{this.tx('This provider is on a job right now. Suspending does not cancel it — reassign the booking first if the job must change hands.', 'مقدم الخدمة في مهمة حالياً. الإيقاف لا يلغيها — أعد إسناد الحجز أولاً إذا لزم تغيير المسؤول.')}</span>
              </div>
            )}
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={this.tx('Search name, phone, ID, area or service', 'ابحث بالاسم أو الهاتف أو المعرّف أو المنطقة أو الخدمة')}
              filters={
                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="inp" value={duty} onChange={this.pick('duty')}
                    aria-label="Filter by duty">
                    <option value="">{this.tx('Duty: all', 'المهمة: الكل')}</option>
                    <option value="job">{this.tx('On job', 'في مهمة')}</option>
                    <option value="idle">{this.tx('Idle', 'متاح')}</option>
                    <option value="offline">{this.tx('Offline', 'غير متصل')}</option>
                  </select>
                  <select className="inp" value={categoryId} onChange={this.pick('categoryId')}
                    aria-label="Filter by service">
                    <option value="">{this.tx('Service: all', 'الخدمة: الكل')}</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className="inp" value={verified} onChange={this.pick('verified')}
                    aria-label="Filter by document status">
                    <option value="">{this.tx('Documents: all', 'المستندات: الكل')}</option>
                    <option value="yes">{this.tx('Verified', 'موثّق')}</option>
                    <option value="pending">{this.tx('Pending', 'قيد الانتظار')}</option>
                    <option value="rejected">{this.tx('Rejected', 'مرفوض')}</option>
                    <option value="suspended">{this.tx('Suspended', 'موقوف')}</option>
                  </select>
                </span>
              }
              emptyText={this.tx('No providers match', 'لا يوجد مقدمو خدمة مطابقون')}
              emptyHint={this.tx('Try clearing the duty, service or document filters.', 'جرّب مسح فلاتر المهمة أو الخدمة أو المستندات.')}
            />
          )}

        {/* Tanda bintang di kolom Area muncul di hampir setiap baris — baru
            satu provider yang mengisi ServiceArea — jadi artinya harus tertulis
            di halaman, bukan cuma di tooltip. */}
        {!error && rows.some(r => r.area && !r.areaFromServiceArea) && (
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', marginTop: 'var(--sp-3)' }}>
            <em style={{ fontStyle: 'normal' }}>*</em> {this.tx('Area taken from the profile city. That provider has not set a service area, so dispatch cannot match them by zone.', 'المنطقة مأخوذة من مدينة الملف الشخصي. لم يحدد مقدم الخدمة منطقة خدمة، لذلك لا يمكن للمناوب مطابقته حسب المنطقة.')}
          </p>
        )}
      </AdminShell>
    );
  }
}

export default injectIntl(Providers);
